/**
 * Статистика каптов: пьедестал, карточки игроков и лента боёв.
 *
 * Мест и рейтинга здесь по-прежнему нет. Топ в Discord намеренно сглажен —
 * он отвечает на вопрос «кто сейчас в форме»; здесь архив с сырыми цифрами,
 * по которым решение принимает человек, а не формула. Пьедестал сверху —
 * витрина тех же сумм, а не второй способ считать: разойтись с каналом
 * нечему, потому что считать тут нечего.
 *
 * Таблицы больше нет: строка со всеми колонками не помещалась на телефон, а
 * заходят сюда в основном с телефонов — по кнопке под топом в канале.
 * Карточка складывается вертикально и читается на любой ширине.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { hasCapability, tierLabel, TIERS, type SessionUser } from '@ak/shared';

import { formatCount, formatDateTime, formatDay, formatRelative } from '../lib/format.ts';
import { useLinkHandler } from '../lib/router.ts';
import {
  captureOutcome,
  captureSide,
  comparePlayers,
  EMPTY_CAPTURE_FILTERS,
  EMPTY_STATS_FILTERS,
  filterCaptures,
  filterStatsPlayers,
  groupCapturesByDay,
  isCaptureFilterActive,
  isStatsFilterActive,
  opponentName,
  opponentOptions,
  OUTCOME_LABEL,
  outcomeColor,
  PLAYER_SORTS,
  readSavedServer,
  saveServer,
  SIDE_LABEL,
  summarizeCaptures,
  type CaptureActivity,
  type CaptureFilters,
  type CaptureOutcomeFilter,
  type CaptureSideFilter,
  type CaptureSummary,
  type FamilySummary,
  type PlayerSortKey,
  type StatsActivity,
  type StatsCapture,
  type StatsFilters,
  type StatsPlayer,
} from '../lib/stats.ts';
import { useResource } from '../lib/useResource.ts';
import { Metric, PlayerRow, Podium } from '../components/player-card.tsx';
import { TerritoryWinrate } from '../components/territory.tsx';
import {
  Button,
  EmptyState,
  FilterChip,
  ErrorState,
  Field,
  PageHeader,
  Panel,
  SegmentedControl,
  Select,
  Skeleton,
  TextInput,
} from '../components/ui.tsx';

const number = (value: number): string => Math.round(value).toLocaleString('ru');

/** Поле порога: пустое значит «порога нет», а не ноль. */
function ThresholdInput({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number | null;
  step?: string;
  onChange: (value: number | null) => void;
}): ReactNode {
  return (
    <Field label={label}>
      <TextInput
        type="number"
        min="0"
        step={step ?? '1'}
        placeholder="любой"
        value={value === null ? '' : String(value)}
        onChange={(event) => {
          const raw = event.target.value.trim();
          const parsed = Number(raw);
          onChange(raw === '' || Number.isNaN(parsed) ? null : parsed);
        }}
      />
    </Field>
  );
}

function PlayersView({
  players,
  activity,
  timezone,
  canChangeTier,
}: {
  players: StatsPlayer[];
  activity: CaptureActivity;
  timezone: string;
  canChangeTier: boolean;
}): ReactNode {
  const [filters, setFilters] = useState<StatsFilters>(EMPTY_STATS_FILTERS);
  /*
   * По умолчанию — киллы: так решил владелец. Пьедестал отвечает «кто больше
   * набил», а не «кто нанёс больше урона», и первая тройка считается тем же
   * числом, которое в её карточках стоит первым.
   */
  const [sortKey, setSortKey] = useState<PlayerSortKey>('kills');

  const filtered = isStatsFilterActive(filters);
  const shown = useMemo(
    () => filterStatsPlayers(players, filters).sort((left, right) => comparePlayers(left, right, sortKey, true)),
    [players, filters, sortKey],
  );

  if (players.length === 0) {
    return (
      <Panel>
        <EmptyState title="Каптов пока нет" description="Цифры появятся, когда бот запишет первый капт нашей семьи." />
      </Panel>
    );
  }

  const patch = (change: Partial<StatsFilters>): void => {
    setFilters((current) => ({ ...current, ...change }));
  };

  const toggleTier = (tier: number): void => {
    patch({
      tiers: filters.tiers.includes(tier) ? filters.tiers.filter((item) => item !== tier) : [...filters.tiers, tier],
    });
  };

  return (
    <>
      <Panel className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <Field label="Поиск">
              <TextInput
                type="search"
                value={filters.query}
                placeholder="Ник, статик или имя в Discord"
                onChange={(event) => {
                  patch({ query: event.target.value });
                }}
              />
            </Field>
          </div>

          <Field label="Был на капте">
            <SegmentedControl<StatsActivity>
              label="Активность"
              value={filters.activity}
              onChange={(value) => {
                patch({ activity: value });
              }}
              options={[
                { value: 'all', label: 'Когда угодно' },
                { value: 'week', label: 'За неделю' },
                { value: 'month', label: 'За месяц' },
                { value: 'stale', label: 'Давно не был' },
              ]}
            />
          </Field>

          <Field label="Сортировать по">
            <Select
              value={sortKey}
              onChange={(event) => {
                setSortKey(event.target.value as PlayerSortKey);
              }}
            >
              {PLAYER_SORTS.map((sort) => (
                <option key={sort.key} value={sort.key}>
                  {sort.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <span className="mb-1.5 block text-sm font-medium">Тир</span>
            <div className="flex flex-wrap gap-2">
              {TIERS.map((tier) => (
                <div key={tier} className="w-28">
                  <FilterChip
                    label={tierLabel(tier)}
                    color={`var(--tier-${String(tier)})`}
                    active={filters.tiers.includes(tier)}
                    onSelect={() => {
                      toggleTier(tier);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="w-28">
            <ThresholdInput
              label="Каптов от"
              value={filters.minCaptures}
              onChange={(value) => {
                patch({ minCaptures: value });
              }}
            />
          </div>
          <div className="w-28">
            <ThresholdInput
              label="Урон/капт от"
              value={filters.minAvgDamage}
              step="50"
              onChange={(value) => {
                patch({ minAvgDamage: value });
              }}
            />
          </div>
          <div className="w-28">
            <ThresholdInput
              label="Киллы/капт от"
              value={filters.minAvgKills}
              step="0.5"
              onChange={(value) => {
                patch({ minAvgKills: value });
              }}
            />
          </div>
          <div className="w-28">
            <ThresholdInput
              label="Побед от, %"
              value={filters.minWinRate}
              step="5"
              onChange={(value) => {
                patch({ minWinRate: value });
              }}
            />
          </div>

          {filtered && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => {
                setFilters(EMPTY_STATS_FILTERS);
              }}
            >
              Сбросить
            </Button>
          )}
        </div>

        {filtered && (
          <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
            Показано {shown.length} из {players.length}
          </p>
        )}
      </Panel>

      {/* Пьедестал — только пока отбор пуст: тройка внутри выборки называла бы
          топом семьи то, что им не является. */}
      {!filtered && shown.length >= 3 && <Podium players={shown.slice(0, 3)} timezone={timezone} />}

      {shown.length === 0 ? (
        <Panel>
          <EmptyState title="Никого не нашлось" description="Попробуйте ослабить отбор или очистить поиск." />
        </Panel>
      ) : (
        <div className="flex flex-col gap-2">
          {(filtered || shown.length < 3 ? shown : shown.slice(3)).map((player, index) => (
            <PlayerRow
              key={player.staticId}
              player={player}
              place={filtered ? null : index + (shown.length < 3 ? 1 : 4)}
              timezone={timezone}
              activity={activity}
              canChangeTier={canChangeTier}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Карточка боя.
 *
 * Исход виден трижды: полосой слева, плашкой у имени противника и подложкой.
 * Одним цветом победу от поражения отличают не все, а лента читается сверху
 * вниз по краю.
 */
function CaptureCard({ capture, timezone }: { capture: StatsCapture; timezone: string }): ReactNode {
  const onLinkClick = useLinkHandler();
  const outcome = captureOutcome(capture);
  const color = outcomeColor(outcome);
  const side = captureSide(capture);

  return (
    <a
      href={`/stats/captures/${String(capture.captureId)}`}
      onClick={onLinkClick}
      className="relative flex flex-wrap items-center gap-x-4 gap-y-3 overflow-hidden rounded-xl py-3.5 pr-4 pl-5 transition-transform hover:-translate-y-0.5"
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-raised)',
        borderLeft: `3px solid ${color}`,
      }}
    >
      {/* Подложка в цвете исхода гаснет к правому краю, чтобы не мешать цифрам. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(90deg, color-mix(in oklab, ${color} 12%, transparent), transparent 55%)`,
        }}
      />

      <div className="relative min-w-[11rem] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold">{opponentName(capture)}</span>
          <span
            className="rounded-md px-2 py-0.5 text-[0.65rem] font-semibold tracking-wide uppercase"
            style={{ color, background: `color-mix(in oklab, ${color} 16%, transparent)` }}
          >
            {OUTCOME_LABEL[outcome]}
          </span>
          <span
            className="rounded-md px-2 py-0.5 text-[0.65rem] tracking-wide uppercase"
            style={{ color: 'var(--text-muted)', background: 'var(--surface-hover)' }}
          >
            {SIDE_LABEL[side]}
          </span>
        </div>
        <div className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          {formatDateTime(capture.startedAt, timezone)} · {formatRelative(capture.startedAt)} · капт{' '}
          {capture.captureId}
        </div>
      </div>

      {/*
        Ширина колонок задана жёстко, числа прижаты вправо: иначе блок цифр
        начинается там, где кончилось имя противника, и в соседних строках
        колонки стоят на разном отступе.
      */}
      <div className="relative grid w-full grid-cols-3 gap-x-4 sm:w-72 sm:shrink-0">
        <Metric align="right" label="состав" value={number(capture.players)} />
        <Metric align="right" label="урон" value={number(capture.damage)} />
        <Metric align="right" label="киллы" value={number(capture.kills)} />
      </div>
    </a>
  );
}

/** Плитка сводки над лентой. */
function SummaryTile({ label, value, tone }: { label: string; value: string; tone?: string | undefined }): ReactNode {
  return (
    <div
      className="rounded-xl px-4 py-3"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
    >
      <div
        className="text-xl font-semibold tabular-nums"
        style={{ color: tone ?? 'var(--text-primary)', fontFamily: 'var(--font-rounded)' }}
      >
        {value}
      </div>
      <div className="text-[0.65rem] tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
    </div>
  );
}

/**
 * Сводка по тому, что сейчас отобрано.
 *
 * Полоса под плитками показывает соотношение побед и поражений: доля в
 * процентах говорит то же самое, но глазом две ленты за разные периоды
 * сравнить по полосе быстрее.
 */
function CaptureSummaryPanel({ summary }: { summary: CaptureSummary }): ReactNode {
  const decided = summary.wins + summary.losses;

  return (
    <Panel className="mb-4 p-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryTile label="каптов" value={String(summary.total)} />
        <SummaryTile label="побед" value={String(summary.wins)} tone="var(--positive)" />
        <SummaryTile label="поражений" value={String(summary.losses)} tone="var(--danger)" />
        <SummaryTile
          label="доля побед"
          value={summary.winRate === null ? '—' : `${String(Math.round(summary.winRate * 100))}%`}
          tone={summary.winRate !== null && summary.winRate >= 0.5 ? 'var(--positive)' : undefined}
        />
        <SummaryTile label="урона" value={number(summary.damage)} />
        <SummaryTile label="киллов" value={number(summary.kills)} />
      </div>

      {decided > 0 && (
        <div className="mt-3">
          <div className="flex h-2 overflow-hidden rounded-full" style={{ background: 'var(--surface-hover)' }}>
            <span style={{ width: `${String((summary.wins / decided) * 100)}%`, background: 'var(--positive)' }} />
            <span style={{ width: `${String((summary.losses / decided) * 100)}%`, background: 'var(--danger)' }} />
          </div>
          <p className="mt-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
            {summary.unknown === 0
              ? `${String(decided)} каптов с отмеченным итогом`
              : `${String(decided)} каптов с отмеченным итогом, ещё ${String(summary.unknown)} без него`}
          </p>
        </div>
      )}
    </Panel>
  );
}

const OUTCOME_FILTERS: [CaptureOutcomeFilter, string, string][] = [
  ['all', 'Все', 'var(--accent)'],
  ['win', 'Победы', 'var(--positive)'],
  ['loss', 'Поражения', 'var(--danger)'],
  ['unknown', 'Без итога', 'var(--text-muted)'],
];

/*
 * Защита и атака — не победа и не поражение, поэтому они не зелёные и не
 * красные: своя пара цветов, чтобы кнопки двух рядов не путались между собой.
 */
const SIDE_FILTERS: [CaptureSideFilter, string, string][] = [
  ['all', 'Любая', 'var(--accent)'],
  ['defence', 'В защите', 'var(--tier-3)'],
  ['attack', 'В атаке', 'var(--warning)'],
];

function CapturesView({ captures, timezone }: { captures: StatsCapture[]; timezone: string }): ReactNode {
  const [filters, setFilters] = useState<CaptureFilters>(EMPTY_CAPTURE_FILTERS);

  const opponents = useMemo(() => opponentOptions(captures), [captures]);
  const shown = useMemo(() => filterCaptures(captures, filters), [captures, filters]);
  const summary = useMemo(() => summarizeCaptures(shown), [shown]);
  // Лента разложена по дням: капты идут вечерами подряд, и без разделителей
  // один вечер сливается со следующим.
  const days = useMemo(
    () => groupCapturesByDay(shown, (capture) => formatDay(capture.startedAt, timezone)),
    [shown, timezone],
  );

  if (captures.length === 0) {
    return (
      <Panel>
        <EmptyState title="Каптов пока нет" description="Бот запишет их сам после ближайшего капта." />
      </Panel>
    );
  }

  const patch = (change: Partial<CaptureFilters>): void => {
    setFilters((current) => ({ ...current, ...change }));
  };

  return (
    <>
      <CaptureSummaryPanel summary={summary} />

      <Panel className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <Field label="Поиск">
              <TextInput
                type="search"
                value={filters.query}
                placeholder="Противник или номер капта"
                onChange={(event) => {
                  patch({ query: event.target.value });
                }}
              />
            </Field>
          </div>

          <div>
            <span className="mb-1.5 block text-sm font-medium">Исход</span>
            <div className="flex flex-wrap gap-2">
              {OUTCOME_FILTERS.map(([value, label, color]) => (
                <div key={value} className="w-28">
                  <FilterChip
                    label={label}
                    color={color}
                    active={filters.outcome === value}
                    onSelect={() => {
                      patch({ outcome: value });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-sm font-medium">Сторона</span>
            <div className="flex flex-wrap gap-2">
              {SIDE_FILTERS.map(([value, label, color]) => (
                <div key={value} className="w-28">
                  <FilterChip
                    label={label}
                    color={color}
                    active={filters.side === value}
                    onSelect={() => {
                      patch({ side: value });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <Field label="Противник">
            <Select
              value={filters.opponent ?? ''}
              onChange={(event) => {
                patch({ opponent: event.target.value === '' ? null : event.target.value });
              }}
            >
              <option value="">Любой</option>
              {opponents.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>

          {isCaptureFilterActive(filters) && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => {
                setFilters(EMPTY_CAPTURE_FILTERS);
              }}
            >
              Сбросить
            </Button>
          )}
        </div>
      </Panel>

      {shown.length === 0 ? (
        <Panel>
          <EmptyState title="Каптов не нашлось" description="Попробуйте другой отбор." />
        </Panel>
      ) : (
        <div className="flex flex-col gap-5">
          {days.map((day) => (
            <div key={day.day}>
              <div className="mb-2 flex items-center gap-3">
                <span className="text-sm font-semibold">{day.day}</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {formatCount(day.captures.length, 'капт', 'капта', 'каптов')}
                </span>
                <span className="h-px flex-1" style={{ background: 'var(--border-subtle)' }} />
              </div>

              <div className="flex flex-col gap-2">
                {day.captures.map((capture) => (
                  <CaptureCard key={capture.captureId} capture={capture} timezone={timezone} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function StatsPage({ user, timezone }: { user: SessionUser; timezone: string }): ReactNode {
  const [tab, setTab] = useState<'players' | 'captures'>('players');
  // Пусто — сервер выберет API (первый из настройки).
  const [server, setServer] = useState<string>(readSavedServer);
  const query = server === '' ? '' : `?server=${encodeURIComponent(server)}`;

  const players = useResource<{
    players: StatsPlayer[];
    activity: CaptureActivity;
    family: FamilySummary;
    servers?: string[];
    server?: string | null;
  }>(`/api/stats/players${query}`);
  const captures = useResource<{ captures: StatsCapture[]; family: FamilySummary }>(`/api/stats/captures${query}`);

  const servers = players.data?.servers ?? [];
  const shownServer = players.data?.server ?? (server === '' ? null : server);
  const chooseServer = (next: string): void => {
    setServer(next);
    saveServer(next);
  };

  const active = tab === 'players' ? players : captures;
  if (active.error !== null) {
    return <ErrorState message={active.error} onRetry={active.reload} />;
  }

  const family = players.data?.family ?? captures.data?.family ?? null;
  const activity: CaptureActivity = players.data?.activity ?? { week: 0, month: 0 };

  return (
    <>
      <PageHeader
        title="Статистика"
        description={
          (family?.name == null
            ? 'Капты семьи: урон, киллы и составы'
            : `Капты семьи ${family.name}: урон, киллы и составы`) +
          (shownServer === null ? '' : ` · сервер ${shownServer}`)
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {/* Серверы считаются отдельно: цифры Mirror и Blaine не смешиваются. */}
            {servers.length > 1 && shownServer !== null && (
              <SegmentedControl<string>
                label="Сервер"
                value={shownServer}
                onChange={chooseServer}
                options={servers.map((name) => ({ value: name, label: name }))}
              />
            )}
            <SegmentedControl<'players' | 'captures'>
              label="Раздел статистики"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'players', label: 'Игроки' },
                { value: 'captures', label: 'Капты' },
              ]}
            />
          </div>
        }
      />

      {/* Справа — винрейт по терам ВЗП; на узком экране он встаёт сверху. */}
      <div className="flex flex-col gap-4 lg:flex-row-reverse lg:items-start">
        <aside className="w-full shrink-0 lg:sticky lg:top-4 lg:w-80">
          <TerritoryWinrate />
        </aside>

        <div className="min-w-0 flex-1">
          {active.loading || active.data === null ? (
            <Panel>
              <Skeleton rows={6} />
            </Panel>
          ) : tab === 'players' ? (
            <PlayersView
              players={players.data?.players ?? []}
              activity={activity}
              timezone={timezone}
              canChangeTier={hasCapability(user.level, 'players.roles')}
            />
          ) : (
            <CapturesView captures={captures.data?.captures ?? []} timezone={timezone} />
          )}
        </div>
      </div>
    </>
  );
}
